import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'bootstrap';
import { Button, CheckBox, TextInput } from 'shamrock-clover-ui';
import { debounce } from '@shamrock-core/common/utils';
import { emailService, TemplateEmailBody } from '../../services/email.service';
import rtsproUserService from '../../services/rtsproUser.service';
import { setUser } from '../../redux/actions/appActions';
import Fallback from '../core/fallback';
import { padDateString } from '../../utils/utils';
import { User } from 'interfaces/user';
import css from './styles/trialExtensionForm.scss';

const Config = require('Config');

const trialExtensionTemplateId = 'd-1e3568ef7b6e4bbd97a763c119c652cc';

interface FormInput {
  fullName: FormField,
  phone: FormField,
  companyName: FormField,
  mcOrDotNumber: FormField,
  numberOfTrucks: FormField;
  interestedIn: FormField;
}

interface FormField {
  value: string,
  required: boolean,
}

interface FormErrors {
  fullName: string,
  phone: string,
  companyName: string,
  mcOrDotNumber: string,
  interestedIn: string,
}

const formatInterestedIn = (interestedIn: string) => {
  const services = interestedIn.split(',');
  const formattedServices = services.map((service) => {
    switch (service) {
      case 'pt':
        return 'ProTransport Basic';
      case 'c':
        return 'Credit';
      default:
        return '';
    }
  });

  return formattedServices.join(', ');
};

export const TrialExtensionForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector<RootState, User>(state => state.app.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [submitStarted, setSubmitStarted] = useState(false);
  const features = useSelector<RootState, Array<string>>((state) => state.app.features);
  const ptTrialEnabled = features.includes('05d3e89dfe967c6ec8fcf9314b155dff0543397f10dbf98279656312ca47b70e');
  const newDeviceInfoUUIDEnabled = features.includes('f4b1b895b37bc2ce1b552e781369cab4bd0f3d732bad6a88f3059a7341e74dfd');

  const [formInput, setFormInput] = useState<FormInput>({
    fullName: {
      value: '',
      required: true,
    },
    phone: {
      value: user.phone ?? '',
      required: true,
    },
    companyName: {
      value: '',
      required: true,
    },
    mcOrDotNumber: {
      value: ptTrialEnabled ? user.mc_number ?? user.dot_number ?? '' : '',
      required: ptTrialEnabled,
    },
    numberOfTrucks: {
      value: '',
      required: false,
    },
    interestedIn: {
      value: '',
      required: true,
    }
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    fullName: '',
    phone: '',
    companyName: '',
    mcOrDotNumber: '',
    interestedIn: '',
  });
  useEffect(() => {
    const missingRequiredField = Object.values(formInput)
      .filter(value => value.required)
      .some(({ value }) => [null, ''].includes(value));
    const hasErrors = Object.values(formErrors).some(value => value !== '');

    setSubmitDisabled(missingRequiredField || hasErrors);
  }, [formErrors, formInput]);

  const updateErrors = (fieldName: string, message: string) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [fieldName]: message,
    }));
  };

  const displayPhoneNumber = () => {
    const phoneValue = formInput.phone.value;
    if (phoneValue.length < 4) return phoneValue;

    const phone = [
      phoneValue.substring(0, 3),
      phoneValue.substring(3, 6),
      phoneValue.substring(6, 10),
    ].filter(value => value);

    return phone.reduce((prevValue, currValue, index) => (
      index === 1 ? `(${prevValue}) ${currValue}` : `${prevValue}-${currValue}`
    ));
  };

  const validateInput = useCallback(debounce((fieldName: keyof FormInput, value: string) => {
    let errorMessage = '';

    switch (fieldName) {
      case 'fullName':
        if (value.length < 3) {
          errorMessage = t('please_enter_a_valid_name');
        }
        break;

      case 'companyName':
        if (value.length < 3) {
          errorMessage = t('please_enter_a_valid_company_name');
        }
        break;

      case 'phone':
        if (value.startsWith('1') || value.length < 10) {
          errorMessage = t('invalid_ten_digit_mobile_phone_number');
        }
        break;

      case 'mcOrDotNumber':
        if (value.length < 6 && value.length != 0) {
          errorMessage = t('invalid_mc_or_dot_number');
        }
        break;

      case 'interestedIn':
        if (value.length < 1) {
          errorMessage = t('trial_extension_modal.select_services');
        }
        break;

      // No need to update errors for something without validation
      default:
        return;
    }

    updateErrors(fieldName, errorMessage);
  }, 500), []);

  const updateInput = (event, fieldName: keyof FormInput) => {
    let value = event.target.value;
    if (fieldName === 'phone') {
      value = value.replace(/\D/g, '').substring(0, 10);
    } else if (fieldName === 'mcOrDotNumber') {
      value = value.replace(/\D/g, '').substring(0, 8);
    } else if (fieldName === 'numberOfTrucks') {
      value = value.replace(/\D/g, '');
    }
    validateInput(fieldName, value);
    setFormInput((prevFormInput) => ({
      ...prevFormInput,
      [fieldName]: {
        ...prevFormInput[fieldName],
        value,
      },
    }));
  };

  const handleInterestedIn = (serviceName: string) => {
    const services = formInput.interestedIn.value.split(',');
    const serviceLocation = services.findIndex((s) => s === serviceName);
    if (serviceLocation > -1) {
      services.splice(serviceLocation, 1);
    } else if (services[0].length < 1) {
      services[0] = serviceName;
    } else {
      services.push(serviceName);
    }

    const newValue = services.join(',');
    updateInput({ target: { value: newValue } }, 'interestedIn');
  };
  const submit = async () => {
    setSubmitStarted(true);
    try {
      const dateString = new Date(parseInt(user?.createdOn));
      const formattedCreatedOn = dateString ?
        `${padDateString(dateString.getMonth() + 1)}/${padDateString(dateString.getDate())}/${dateString.getFullYear()}` :
        '';

      const email: TemplateEmailBody = {
        to: Config.trialLeadEmail,
        from: 'noreply@rtspro.com',
        cc: user.email,
        templateId: trialExtensionTemplateId,
        dynamicTemplateData: {
          fullName: formInput.fullName.value,
          companyName: formInput.companyName.value,
          email: user.email,
          phoneNumber: formInput.phone.value,
          companyNumber: formInput.mcOrDotNumber.value,
          numberOfTrucks: formInput.numberOfTrucks.value,
          interestedIn: formatInterestedIn(formInput.interestedIn.value),
          createdOn: formattedCreatedOn,
        },
      };
      await emailService.sendTemplateEmail(email);

      const updatePayload = { trialExtensionRequested: true, ...userInputToDoc(formInput) };
      if (newDeviceInfoUUIDEnabled && user.userStatus?.isRegistered) {
        await rtsproUserService.updateUser({ email: user.email }, updatePayload);
      } else {
        await rtsproUserService.updateTrialUser(user.email, updatePayload);
      }

      const newUser = { ...user, trialExtensionRequested: true };
      dispatch(setUser(newUser));

      navigate(`${location.pathname}`, { state: null });
    } catch (error) {
    } finally {
      setSubmitStarted(false);
    }
  };

  return (
    <div className={css.wrapper}>
      {submitStarted && <Fallback />}
      <div className={css.inputWrapper}>
        <div>
          <p className={css.headerText}>{t('trial_extension_modal.form_header')}</p>
          <p className={css.bodyText}>{t('trial_extension_modal.form_body')}</p>
        </div>
        <div className={css.inputRow}>
          <TextInput
            id="fullName"
            variant="filled"
            className={css.inputRowTextInput}
            label={`${t('full_name')}*`}
            value={formInput.fullName.value}
            error={formErrors.fullName}
            onChange={(event) => updateInput(event, 'fullName')}
          />
          <TextInput
            id="phone"
            variant="filled"
            className={css.inputRowTextInput}
            label={`${t('phone')}*`}
            value={displayPhoneNumber()}
            error={formErrors.phone}
            onChange={(event) => updateInput(event, 'phone')}
          />
        </div>
        <div className={css.inputRow}>
          <TextInput
            id="companyName"
            variant="filled"
            className={css.inputRowTextInput}
            label={`${t('company_name')}*`}
            value={formInput.companyName.value}
            error={formErrors.companyName}
            onChange={(event) => updateInput(event, 'companyName')}
          />
          <TextInput
            id="mcNumber"
            variant="filled"
            className={css.inputRowTextInput}
            label={ptTrialEnabled ? `${t('dot_or_mc_required')}*` : t('dot_or_mc_optional')}
            maxLength={8}
            value={String(formInput.mcOrDotNumber.value)}
            error={formErrors.mcOrDotNumber}
            onChange={(event) => updateInput(event, 'mcOrDotNumber')}
          />
        </div>
        <div className={css.inputRow}>
          <TextInput
            id="numberOfTrucks"
            variant="filled"
            className={css.inputRowTextInput}
            label={t('number_of_trucks_optional')}
            value={formInput.numberOfTrucks.value}
            onChange={(event) => updateInput(event, 'numberOfTrucks')}
          />
        </div>
        <div className={css.checkboxBody}>
          <p className={css.checkboxBodyText}>{t('trial_extension_modal.services_interested_in')}</p>
          <CheckBox
            checked={formInput.interestedIn.value.includes('pt')}
            text={t('protransport_basic')}
            onClick={() => handleInterestedIn('pt')}
          />
          <CheckBox
            checked={formInput.interestedIn.value.includes('c')}
            text={t('trial_extension_modal.credit')}
            onClick={() => handleInterestedIn('c')}
          />
        </div>
      </div>
      <div className={css.submitButton}>
        <Button width='100%' disabled={submitDisabled || submitStarted} onClick={submit}>{t('submit')}</Button>
      </div>
    </div>
  );
};

const userInputToDoc = (formInput: FormInput): Partial<User> => {
  return ({
    firstname: formInput.fullName.value.split(' ').at(0),
    lastname: formInput.fullName.value.split(' ').at(1),
    company: formInput.companyName.value,
    phone: formInput.phone.value,
    mc_number: formInput.mcOrDotNumber.value,
    dot_number: formInput.mcOrDotNumber.value,
  });
};
